<script context="module" lang="ts">
    import type {
        VideoArtworkDictionary,
        VideoArtworkDictionaryItem,
    } from '~/jet/models';

    export function bestVideoAsset(
        lockupTypeId: string,
        videoSelections: VideoArtworkDictionary,
        mediaQuery: string,
    ): VideoArtworkDictionaryItem | undefined {
        if (!videoSelections) return;

        if (mediaQuery === 'xsmall') {
            // TODO: Temporary until Music/Classical supports full bleed ubers
            //       Phone selection is tall video but we want to keep it square for now
            //       rdar://151875484 (Onyx Music: Implement mobile Uber detail headers on product detail pages)
            if (lockupTypeId === 'containerDetailHeaderLockup') {
                return videoSelections['tablet'];
            }

            return videoSelections['phone'];
        }

        return videoSelections['tablet'];
    }
</script>

<script lang="ts">
    import Artwork from './Artwork.svelte';
    import { loggerFor } from '@amp/web-apps-logger';
    import MotionVideo from '@amp/web-app-components/src/components/Artwork/MotionVideo.svelte';
    import type {
        Artwork as ArtworkType,
        ContentKind,
        SectionItemKind,
        VideoArtwork as VideoArtworkType,
    } from '~/jet/models';
    import { mediaQueries } from '@amp/web-app-components/src/stores/media-query';

    export let alt: string = '';
    export let artwork: ArtworkType | null;
    export let disableAutoCenter: boolean = false;
    export let fallbackKind: ContentKind | null = null;
    export let forceAnimation: boolean = false;
    export let profile: string;
    export let itemKind: SectionItemKind;
    export let videoArtwork: VideoArtworkType | null;
    export let viewportThreshold: number | null = null;
    export let videoCover: boolean = false;
    export let videoAsset: VideoArtworkDictionaryItem | null = null;

    // Settings to pass to Artwork component
    export let forceCropCode: boolean = false;
    export let useContainerStyle: boolean = false;

    const log = loggerFor('<MotionVideo>');

    $: videoSrc = videoAsset?.video;
    $: videoAsset = bestVideoAsset(
        itemKind,
        videoArtwork?.dictionary,
        $mediaQueries,
    );
    // If provided video object does not result in a usable video asset, fall back to provided artwork object
    $: staticArtworkObj = videoAsset ? videoAsset.previewFrame : artwork;

    $: (() => {
        // if (!videoArtwork && !collageArtwork && !artwork) {
        if (!videoArtwork && !artwork) {
            log.warn('missing a valid artwork or video source for: ', itemKind);
        }
    })();
</script>

{#if videoArtwork}
    <div class="artwork-container">
        <Artwork
            artwork={staticArtworkObj}
            {profile}
            {useContainerStyle}
            {disableAutoCenter}
            {forceCropCode}
            {alt}
            {fallbackKind}
            forceFullWidth={false}
        />

        {#if videoSrc}
            <div data-testid="video-artwork" class="video-artwork__container">
                <MotionVideo
                    {videoSrc}
                    {forceAnimation}
                    {viewportThreshold}
                    {videoCover}
                />
            </div>
        {:else}
            {log.warn(
                'missing Motion video source: ',
                videoAsset,
                videoArtwork,
            )}
        {/if}
    </div>
{:else}
    <Artwork
        {artwork}
        {profile}
        {useContainerStyle}
        {disableAutoCenter}
        {forceCropCode}
        {alt}
        {fallbackKind}
        forceFullWidth={false}
    />
{/if}

<style lang="scss">
    @use '@amp/web-shared-styles/sasskit-stylekit/ac-sasskit-config';
    @use 'ac-sasskit/core/viewports' as *;
    @use 'ac-sasskit/core/selectors' as *;
    @use 'amp/stylekit/core/mixins/browser-targets' as *;
    @use '@amp/web-shared-styles/app/core/viewports' as *;
    @use '@amp/web-shared-styles/app/core/globalvars' as *;

    .artwork-container {
        // Inherit `border-radius` from parent, which may vary (ex Superhero, product hero)
        border-radius: inherit;
        position: relative;
        z-index: var(--z-default);

        :global(.artist-header--video) & {
            --artist-header-aspect-ratio: #{$fixed-artwork-header-ratio-sm};
            --artwork-override-width: 100vw;
            --artwork-override-height: calc(
                var(--artist-header-aspect-ratio) /
                    var(--artwork-override-width)
            );
            --artwork-override-min-height: #{$artist-header-height-xs};
            --artwork-override-max-width: #{viewport-get-property-for(
                    xlarge,
                    content
                )}; // Safari needs max dimensions
            --artwork-override-max-height: calc(
                #{$fixed-artwork-header-ratio-lg} / #{viewport-get-property-for(
                        xlarge,
                        content
                    )}
            ); // Safari needs max dimensions
            // Always-on scrollbars causes alignment issue on most browsers with `position: fixed`.
            // Using `position: static;` here instead.
            position: static;
            z-index: var(--z-default);
            inset-inline-start: auto;

            // TODO: rdar://94808466 (JMOTW: Implement Artist Fixed Headers)
            // @media (--target-touchscreen) {
            //     // `position: fixed` works fine on Android
            //     position: fixed;
            // }

            // @include target-safari {
            //     // `position: fixed` works fine on Safari
            //     position: fixed;
            // }

            @media (--sidebar-visible) {
                // Set aspect ratios for smaller screens
                --artwork-override-width: #{calc(
                        100vw - var(--web-navigation-width)
                    )};
                --artwork-override-height: #{calc(
                        (var(--artist-header-aspect-ratio)) /
                            var(--artwork-override-width)
                    )};
                min-height: var(--artwork-override-min-height);
                max-width: var(--artwork-override-max-width);
                max-height: var(
                    --artwork-override-max-height
                ); // Safari needs max dimensions
                height: var(--artwork-override-height);
                width: var(--artwork-override-width);
            }

            @media (--medium) {
                // Set aspect ratios for larger screens
                --artist-header-aspect-ratio: #{$fixed-artwork-header-ratio-lg};
            }
        }
    }

    :global(
            .artist-header--video .artwork-container .artwork-component__image
        ) {
        object-fit: cover;
        object-position: top;

        @include target-safari {
            @include feature-detect(is-refocused) {
                // Avoid a Safari painting issue, where artwork is not visible on refocus
                // Workaround for rdar://95827237 (Artwork not visible on window refocus on Apple Music Artist pages)
                will-change: opacity;
            }
        }
    }

    .video-artwork__container {
        // Note:  We are not setting a background-color here because doing so will block out
        //        the underlying static artwork when a video is not playing.
        width: 100%;
        height: 100%;
        pointer-events: none;
        position: absolute;
        top: 0;
        // Inherit `border-radius` from parent, which may vary (ex Superhero, product hero)
        border-radius: inherit;
        z-index: var(--z-default);

        @media (--small) {
            .circular-artwork {
                display: none;
            }
        }

        :global(.artist-header--video) & {
            min-height: $artist-header-height-xs;
            left: 50%; // RTL not needed
            transform: translateX(-50%);
            -webkit-box-reflect: above; // Reflect image under web chrome
        }
    }
</style>
